<script>
export default {
  name: 'SendButtonsModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'send'],
  data() {
    return {
      body: '',
      footer: '',
      buttons: [{ id: '1', text: '' }],
      isSending: false,
    };
  },
  computed: {
    validButtons() {
      return this.buttons.filter(b => b.text.trim().length > 0);
    },
    isValid() {
      return this.body.trim().length > 0 && this.validButtons.length > 0;
    },
  },
  methods: {
    onClose() {
      this.resetForm();
      this.$emit('close');
    },
    addButton() {
      if (this.buttons.length < 3) {
        this.buttons.push({ id: String(this.buttons.length + 1), text: '' });
      }
    },
    removeButton(idx) {
      this.buttons.splice(idx, 1);
      this.buttons.forEach((b, i) => {
        b.id = String(i + 1);
      });
    },
    send() {
      if (!this.isValid) return;
      this.isSending = true;

      const buttonsPayload = this.validButtons.map(b => ({
        ButtonId: b.id,
        ButtonText: b.text.trim(),
      }));

      this.$emit('send', {
        type: 'buttons',
        title: this.body.trim(),
        buttons: buttonsPayload,
        footer: this.footer.trim() || undefined,
      });

      this.isSending = false;
      this.onClose();
    },
    resetForm() {
      this.body = '';
      this.footer = '';
      this.buttons = [{ id: '1', text: '' }];
      this.isSending = false;
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="onClose">
    <div class="flex flex-col p-6">
      <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
        {{ $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.TITLE') }}
      </h3>

      <!-- Body -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.BODY') }} *
        </label>
        <textarea
          v-model="body"
          class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-2 focus:ring-2 focus:ring-woot-500 focus:border-woot-500 min-h-[80px]"
          :placeholder="
            $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.BODY_PLACEHOLDER')
          "
          maxlength="1024"
        />
        <span class="text-xs text-n-slate-9 mt-1">{{
          `${body.length}/1024`
        }}</span>
      </div>

      <!-- Footer -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-1">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.FOOTER') }}
        </label>
        <input
          v-model="footer"
          type="text"
          class="w-full border border-n-weak rounded-lg px-3 py-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-2 focus:ring-2 focus:ring-woot-500"
          :placeholder="
            $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.FOOTER_PLACEHOLDER')
          "
          maxlength="60"
        />
      </div>

      <!-- Dynamic buttons -->
      <div class="mb-4">
        <label class="block text-sm font-medium text-n-slate-11 mb-2">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.BUTTONS_LABEL') }} *
        </label>

        <div
          v-for="(btn, idx) in buttons"
          :key="idx"
          class="flex items-center gap-2 mb-2"
        >
          <span class="text-sm text-n-slate-9 w-5">{{ idx + 1 }}.</span>
          <input
            v-model="btn.text"
            type="text"
            class="flex-1 border border-n-weak rounded-lg px-3 py-2 text-sm text-n-slate-12 bg-white dark:bg-n-solid-2 focus:ring-2 focus:ring-woot-500"
            :placeholder="
              $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.BUTTON_PLACEHOLDER', {
                n: idx + 1,
              })
            "
            maxlength="20"
          />
          <span class="text-xs text-n-slate-9 w-10 text-right">
            {{ `${btn.text.length}/20` }}
          </span>
          <woot-button
            v-if="buttons.length > 1"
            variant="clear"
            size="tiny"
            color-scheme="alert"
            icon="dismiss"
            @click="removeButton(idx)"
          />
        </div>

        <woot-button
          v-if="buttons.length < 3"
          variant="clear"
          size="small"
          icon="add"
          @click="addButton"
        >
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.ADD_BUTTON') }}
        </woot-button>
        <p class="text-xs text-n-slate-9 mt-1">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.MAX_BUTTONS') }}
        </p>
      </div>

      <!-- Preview -->
      <div class="mb-4 p-3 bg-n-alpha-1 rounded-lg border border-n-weak">
        <p class="text-xs font-medium text-n-slate-9 mb-2">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.PREVIEW') }}
        </p>
        <div
          class="bg-white dark:bg-n-solid-2 rounded-lg p-3 shadow-sm border border-n-weak max-w-[280px]"
        >
          <p class="text-sm text-n-slate-12 mb-2 whitespace-pre-wrap">
            {{ body || '...' }}
          </p>
          <p v-if="footer" class="text-xs text-n-slate-9 mb-2">
            {{ footer }}
          </p>
          <div class="space-y-1.5">
            <div
              v-for="(btn, i) in validButtons"
              :key="i"
              class="text-center py-1.5 text-sm text-n-blue-11 border border-n-blue-6 rounded bg-n-blue-2"
            >
              {{ btn.text }}
            </div>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="flex justify-end gap-2">
        <woot-button variant="clear" @click="onClose">
          {{ $t('CANCEL') }}
        </woot-button>
        <woot-button :disabled="!isValid" :is-loading="isSending" @click="send">
          {{ $t('CONVERSATION.DINASTIA_ACTIONS.BUTTONS.SEND') }}
        </woot-button>
      </div>
    </div>
  </woot-modal>
</template>
